import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DollarSign, TrendingUp, Zap, Clock, Activity } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAppToken } from '@/lib/oauthClient';

export default function AICosts() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [period, setPeriod] = useState(7); // days

  useEffect(() => {
    fetchStats();
    fetchRecentLogs();
  }, [period]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      const res = await axios.get(`${backendUrl}/api/admin/ai-usage/stats?days=${period}`, {
        headers: { 'X-App-Token': appToken }
      });
      
      if (res.data.success) {
        setStats(res.data);
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentLogs = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      const res = await axios.get(`${backendUrl}/api/admin/ai-usage/recent?limit=20`, {
        headers: { 'X-App-Token': appToken }
      });
      
      if (res.data.success) {
        setRecentLogs(res.data.logs || []);
      }
    } catch (error) {
      console.error('Erreur chargement logs:', error);
    }
  };

  const formatCost = (cost) => {
    if (cost === 0) return '$0.00';
    if (cost < 0.01) return `$${(cost * 1000).toFixed(2)}‰`; // millimes
    return `$${cost.toFixed(4)}`;
  };

  const getOperationLabel = (op) => {
    const labels = {
      'classify': 'Classification',
      'faq_response': 'Réponse FAQ',
      'suggest_actions': 'Suggestions',
      'detect_similar': 'Tickets similaires'
    };
    return labels[op] || op;
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  const { total, by_operation, daily_breakdown, average_cost_per_call, top_tickets } = stats || {};

  return (
    <div className="space-y-6 p-6" data-testid="ai-costs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <DollarSign className="h-8 w-8 text-green-600" />
            Coûts IA
          </h1>
          <p className="text-muted-foreground mt-1">
            Suivi des coûts et usage de l'assistant intelligent
          </p>
        </div>
        
        <div className="flex gap-2">
          {[7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                period === d
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700'
              }`}
              data-testid={`period-${d}`}
            >
              {d}j
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Coût Total</p>
                <p className="text-2xl font-bold text-green-600">{formatCost(total?.cost || 0)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600 opacity-50" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {period} derniers jours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Appels Total</p>
                <p className="text-2xl font-bold">{total?.calls || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-600 opacity-50" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {average_cost_per_call ? `${formatCost(average_cost_per_call)} / appel` : '-'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tokens Total</p>
                <p className="text-2xl font-bold">{(total?.total_tokens || 0).toLocaleString()}</p>
              </div>
              <Zap className="h-8 w-8 text-yellow-600 opacity-50" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              In: {(total?.input_tokens || 0).toLocaleString()} / Out: {(total?.output_tokens || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Coût Moyen/Jour</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCost((total?.cost || 0) / period)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-600 opacity-50" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Projection: {formatCost((total?.cost || 0) / period * 30)}/mois
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="operations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="operations">Par opération</TabsTrigger>
          <TabsTrigger value="daily">Évolution journalière</TabsTrigger>
          <TabsTrigger value="recent">Logs récents</TabsTrigger>
        </TabsList>

        {/* By Operation */}
        <TabsContent value="operations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Coûts par type d'opération</CardTitle>
              <CardDescription>Répartition des appels et coûts par fonction IA</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {by_operation && Object.entries(by_operation).map(([op, data]) => (
                  <div key={op} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{getOperationLabel(op)}</p>
                      <p className="text-sm text-muted-foreground">
                        {data.calls} appels • {(data.input_tokens + data.output_tokens).toLocaleString()} tokens
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-lg font-bold">
                      {formatCost(data.cost)}
                    </Badge>
                  </div>
                ))}
                {(!by_operation || Object.keys(by_operation).length === 0) && (
                  <p className="text-center text-muted-foreground py-8">
                    Aucune donnée disponible pour la période sélectionnée
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Breakdown */}
        <TabsContent value="daily" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Évolution journalière</CardTitle>
              <CardDescription>Appels et coûts par jour</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {daily_breakdown && daily_breakdown.map(day => (
                  <div key={day.date} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent">
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{new Date(day.date).toLocaleDateString('fr-FR')}</span>
                      <Badge variant="outline">{day.calls} appels</Badge>
                    </div>
                    <span className="font-bold text-green-600">{formatCost(day.cost)}</span>
                  </div>
                ))}
                {(!daily_breakdown || daily_breakdown.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">
                    Aucune donnée disponible
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recent Logs */}
        <TabsContent value="recent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>20 derniers appels IA</CardTitle>
              <CardDescription>Log temps réel des opérations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {recentLogs.map(log => (
                  <div key={log.id} className="p-3 border rounded-lg text-sm hover:bg-accent">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{getOperationLabel(log.operation)}</span>
                      <Badge variant={log.success ? "default" : "destructive"} className="text-xs">
                        {formatCost(log.estimated_cost)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{log.model}</span>
                      <span>{log.input_tokens + log.output_tokens} tokens</span>
                      <span>{new Date(log.timestamp).toLocaleString('fr-FR')}</span>
                      {log.ticket_id && <span className="text-blue-600">#{log.ticket_id.slice(0, 8)}</span>}
                    </div>
                  </div>
                ))}
                {recentLogs.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    Aucun log disponible
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Savings Info */}
      <Card className="border-green-200 bg-green-50/50 dark:bg-green-950/20">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <DollarSign className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-900 dark:text-green-100">
              <p className="font-semibold mb-1">💰 Économies réalisées</p>
              <p>
                <strong>Optimisé avec GPT-5.4-mini :</strong> Économie de ~75% par rapport à GPT-5.4.
                <br />
                Coût moyen par ticket auto-répondu : <strong>~{formatCost(0.0015)}</strong> (au lieu de ~{formatCost(0.006)})
                <br />
                <strong>Économie vs. agent humain :</strong> ~99,9% par ticket (agent humain ≈ 2-4 €/ticket, IA ≈ {formatCost(0.0015)})
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
